import React, { useState } from 'react';
import styled from 'styled-components';
import {
  IconButton,
  MenuItem,
  Paper,
  Select,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useFeathers } from '../../app/util';
import { FieldButtonToolbar } from '../FieldButtonToolbar';
import {
  configurationForPatch,
  editableConfiguration
} from './configuration';

const EditDefaultRaceConfiguration = ({
  officeId,
  value,
  onChange,
  editingDisabled = false
}) => {
  const feathers = useFeathers();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(() => editableConfiguration(value));

  const startEditing = () => {
    setDraft(editableConfiguration(value));
    setEditing(true);
  };

  const cancelEditing = () => {
    setDraft(editableConfiguration(value));
    setEditing(false);
  };

  const save = async () => {
    setLoading(true);
    try {
      const result = await feathers.getService('offices').patch(officeId, {
        defaultRaceConfiguration: configurationForPatch(draft)
      });
      setEditing(false);
      if(onChange) onChange(result);
    } catch(error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const rankedChoiceState = typeof draft.rankedChoice?.enabled === 'boolean'
    ? (draft.rankedChoice.enabled ? 'enabled' : 'disabled')
    : '';

  return (
    <Paper variant='outlined' sx={{ mt: '30px', p: 3, maxWidth: '800px' }}>
      <Header>
        <Typography variant='h5'>Default race configuration</Typography>
        {editing
          ? (
            <FieldButtonToolbar
              onSave={save}
              onCancel={cancelEditing}
              disabled={loading}
            />
          )
          : (
            !editingDisabled && (
              <IconButton size='small' onClick={startEditing} title='Edit default race configuration'>
                <EditIcon sx={{ width: '18px', height: '18px' }} />
              </IconButton>
            )
          )}
      </Header>

      {editing
        ? (
          <Fields>
            <FieldRow>
              <Typography variant='body1'>Max choices</Typography>
              <Select
                size='small'
                value={draft.maxChoices ?? ''}
                onChange={event => {
                  const maxChoices = event.target.value;
                  setDraft(current => {
                    if(maxChoices === '') {
                      const next = { ...current };
                      delete next.maxChoices;
                      return next;
                    }
                    return { ...current, maxChoices };
                  });
                }}
              >
                <MenuItem value=''><em>None set</em></MenuItem>
                {Array.from({ length: 25 }, (_, index) => index + 1).map(choice => (
                  <MenuItem key={choice} value={choice}>{choice}</MenuItem>
                ))}
              </Select>
            </FieldRow>

            <FieldRow>
              <Typography variant='body1'>Retention election</Typography>
              <Select
                size='small'
                value={typeof draft.retention === 'boolean' ? draft.retention : ''}
                onChange={event => {
                  const retention = event.target.value;
                  setDraft(current => {
                    if(retention === '') {
                      const next = { ...current };
                      delete next.retention;
                      return next;
                    }
                    return { ...current, retention };
                  });
                }}
              >
                <MenuItem value=''><em>None set</em></MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
                <MenuItem value={false}>No</MenuItem>
              </Select>
            </FieldRow>

            <FieldRow>
              <Typography variant='body1'>Ranked choice</Typography>
              <Select
                size='small'
                value={rankedChoiceState}
                onChange={event => {
                  const state = event.target.value;
                  setDraft(current => {
                    if(state === '') {
                      const next = { ...current };
                      delete next.rankedChoice;
                      return next;
                    }
                    return {
                      ...current,
                      rankedChoice: {
                        enabled: state === 'enabled',
                        maxChoices: current.rankedChoice?.maxChoices ?? 1
                      }
                    };
                  });
                }}
              >
                <MenuItem value=''><em>None set</em></MenuItem>
                <MenuItem value='enabled'>Enabled</MenuItem>
                <MenuItem value='disabled'>Disabled</MenuItem>
              </Select>
            </FieldRow>

            {draft.rankedChoice && (
              <FieldRow>
                <Typography variant='body1'>Ranked choice max choices</Typography>
                <Select
                  size='small'
                  value={draft.rankedChoice.maxChoices ?? 1}
                  onChange={event => setDraft(current => ({
                    ...current,
                    rankedChoice: {
                      ...current.rankedChoice,
                      maxChoices: event.target.value
                    }
                  }))}
                >
                  {Array.from({ length: 10 }, (_, index) => index + 1).map(choice => (
                    <MenuItem key={choice} value={choice}>{choice}</MenuItem>
                  ))}
                </Select>
              </FieldRow>
            )}
          </Fields>
        )
        : (
          <Fields>
            <ReadOnlyField label='Max choices' value={value?.maxChoices ?? 'None set'} />
            <ReadOnlyField
              label='Retention election'
              value={typeof value?.retention === 'boolean' ? (value.retention ? 'Yes' : 'No') : 'None set'}
            />
            <ReadOnlyField
              label='Ranked choice'
              value={value?.rankedChoice
                ? (value.rankedChoice.enabled ? 'Enabled' : 'Disabled')
                : 'None set'}
            />
            {value?.rankedChoice && (
              <ReadOnlyField
                label='Ranked choice max choices'
                value={value.rankedChoice.maxChoices ?? 1}
              />
            )}
          </Fields>
        )}
    </Paper>
  );
};

const ReadOnlyField = ({ label, value }) => (
  <FieldRow>
    <Typography variant='body1' color='text.secondary'>{label}</Typography>
    <Typography variant='body1'>{value}</Typography>
  </FieldRow>
);

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Fields = styled.div`
  display: grid;
  gap: 16px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(160px, 1fr);
  align-items: center;
  gap: 16px;
`;

export { EditDefaultRaceConfiguration };
